import type { SetState } from "zustand";

import type { AskSource } from "@performyard/service-contracts";

import type { ChatStatus, ConfidenceLevel, StreamStage } from "../../types";
import type { AskPerformYardState } from "../types";

import { buildGreetingMessage, generateMessageId, getOrCreateSessionId } from "../helpers";
import type { GreetingFlags } from "../helpers";
import { drainPendingChunks, schedulePendingChunk } from "../streaming-buffer";

export const createChatSlice = (set: SetState<AskPerformYardState>) => ({
	sessionId: getOrCreateSessionId(),
	messages: [
		buildGreetingMessage({
			askHrEnabled: false,
			goalCreationEnabled: false,
			formCreationEnabled: false,
			interviewerEnabled: false,
			reportBuilderEnabled: false,
		}),
	],
	status: "idle" as ChatStatus,
	streamStage: null as StreamStage | null,
	streamMessage: null as string | null,
	error: null as string | null,
	inputValue: "",

	addUserMessage: (content: string) => {
		const id = generateMessageId();
		set((state) => ({
			messages: [
				...state.messages,
				{
					id,
					role: "user" as const,
					content,
					timestamp: new Date(),
				},
			],
			inputValue: "",
		}));
		return id;
	},

	addStaticAssistantMessage: (content: string) => {
		const id = generateMessageId();
		set((state) => ({
			messages: [...state.messages, { id, role: "assistant" as const, content, timestamp: new Date() }],
		}));
	},

	startAssistantMessage: () => {
		const id = generateMessageId();
		// Pushes the empty streaming assistant message so the in-message
		// "Thinking…" placeholder can render immediately. Status is intentionally
		// left untouched so callers control the thinking→streaming transition
		// separately (the in-message placeholder reads `streamMessage` regardless,
		// so the bubble keeps showing live tool/status text either way).
		set((state) => ({
			messages: [
				...state.messages,
				{
					id,
					role: "assistant" as const,
					content: "",
					isStreaming: true,
					timestamp: new Date(),
				},
			],
		}));
		return id;
	},

	appendToAssistantMessage: (messageId: string, chunk: string) => {
		schedulePendingChunk(messageId, chunk);
	},

	completeAssistantMessage: (
		messageId: string,
		sources?: AskSource[],
		confidence?: ConfidenceLevel,
		isIncomplete?: boolean
	) => {
		const remainingChunks = drainPendingChunks();

		set((state) => {
			const lastIdx = state.messages.length - 1;
			const lastMsg = state.messages[lastIdx];
			if (!lastMsg || lastMsg.id !== messageId) return state;

			const finalContent = lastMsg.content + remainingChunks;

			// Drop tool-only assistant turns (no content AND no meaningful payload).
			// Tool-only steps with suppressed narration finish with no chunks. Bedrock
			// rejects messages with empty content on re-send, so the backend agent
			// `formatMessages` helpers filter empty turns before they reach the model
			// (mirrors interviewer/handler.ts:44-46). That means we can safely keep
			// empty bubbles here when they own UI payload — the backend will strip
			// them on the next request.
			//
			// Messages own UI payload when they carry sources, an isIncomplete flag,
			// suggestion arrays, OR the active reportBuilder reference (whose
			// `messageId` anchors the saving/error/Open-in-R&A CTAs in ChatMessage).
			// Confidence is NOT user-visible (backend-only signal for source filtering),
			// so it must not preserve an otherwise-empty turn.
			const ownsReportBuilder = state.reportBuilder?.messageId === messageId;
			const hasMeaningfulPayload =
				!!sources?.length ||
				isIncomplete === true ||
				!!lastMsg.competencySuggestions?.length ||
				!!lastMsg.goalSuggestions?.length ||
				!!lastMsg.formSuggestions?.length ||
				!!lastMsg.formQuestionSuggestions?.length ||
				ownsReportBuilder;
			if (finalContent.trim().length === 0 && !hasMeaningfulPayload) {
				return {
					messages: state.messages.slice(0, -1),
					status: "idle" as const,
					streamStage: null,
					streamMessage: null,
				};
			}

			const updated = state.messages.slice();
			updated[lastIdx] = {
				...lastMsg,
				content: finalContent,
				isStreaming: false,
				sources,
				confidence,
				isIncomplete,
			};
			return { messages: updated, status: "idle" as const, streamStage: null, streamMessage: null };
		});
	},

	setStatus: (status: ChatStatus) => set({ status }),
	setStreamStage: (stage: StreamStage | null) => set({ streamStage: stage }),
	setStreamMessage: (message: string | null) => set({ streamMessage: message }),
	setError: (error: string | null) =>
		set((state) => ({
			error,
			status: error ? "error" : state.status === "error" ? "idle" : state.status,
		})),

	updateGreetingMessage: (flags: GreetingFlags) => {
		set((state) => {
			const cached = {
				cachedAskHrEnabled: flags.askHrEnabled,
				cachedGoalCreationEnabled: flags.goalCreationEnabled,
				cachedFormCreationEnabled: flags.formCreationEnabled,
				cachedInterviewerEnabled: flags.interviewerEnabled,
				cachedReportBuilderEnabled: flags.reportBuilderEnabled,
				cachedIsOnFormBuilderPage: flags.isOnFormBuilderPage,
			};
			const greetingIdx = state.messages.findIndex((m) => m.id === "greeting");
			if (greetingIdx === -1) return { messages: state.messages, ...cached };
			const updatedMessages = state.messages.slice();
			updatedMessages[greetingIdx] = buildGreetingMessage(flags);
			return { messages: updatedMessages, ...cached };
		});
	},
});
